/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable @typescript-eslint/no-non-null-assertion */

/* eslint-disable indent */
import { yupResolver } from '@hookform/resolvers/yup';
import type { FC } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '@pulse-web-ui/button';
import { Datepicker } from '@pulse-web-ui/datepicker';
import { HelperText } from '@pulse-web-ui/helper-text';
import { Info } from '@pulse-web-ui/icons';
import { Input } from '@pulse-web-ui/input';
import { useTheme } from '@pulse-web-ui/theme';

import {
  AdaptiveHeadersWrapper,
  Container,
  FormSubTitle,
  InfoWrapper,
  PaymentPeriodCardDescription,
  PaymentPeriodCardTitle,
  RadioDescriptionRender,
  RadioLabelRender,
  Skeleton,
  SubHeaderAdaptiveWarning,
  SubHeadersWrapper,
  WarnigText,
} from '@src/components';
import { sendAnalyticEvent } from '@src/components/web-analytic/utils';
import {
  ApiEndpoints,
  DEFAULT_FIAS_ID,
  FeatureFlags,
  FlatSteps,
  analyticEvents,
  durationDescription,
} from '@src/constants';
import { FIRST_STEP_FOR_SETTINGS } from '@src/constants/preset';
import { FormIFLGetPricesFull } from '@src/constants/request-data';
import {
  GlobalErrorInfo,
  PaymentPeriodHint,
  PaymentPeriodList,
  PaymentPeriodsTitle,
  PromoCodeDiscount,
  SettingsWrapper,
} from '@src/features';
import {
  useBlockNextStep,
  useCustomerEventRegister,
  useHandlePressKey,
  useNextStep,
  useRequest,
  useValidateProfileAuth,
} from '@src/hooks';
import {
  AuthActionTypes,
  AvailableSubscriptionActionTypes,
  CheckUserDataActionTypes,
  IFLHouseActionTypes,
  OrderActionTypes,
  PresetSettingActionTypes,
  Store,
  UserActionTypes,
} from '@src/store';
import { IFLFlatActionTypes } from '@src/store/ifl-flat';
import { WizardActionTypes } from '@src/store/wizard';
import { FormPromoProps, KeyCode, SelectedDuration } from '@src/types';
import {
  addTestAttribute,
  checkDateBefore,
  checkDateBetween,
  checkValidDate,
  convertDateByTimeZone,
  createDateWithTimezone,
  getAdditionalRiskCodesToString,
  getMainRiskCodesToString,
  getPaymentPeriodCardSubtitle,
  parseProductCode,
  resetPromo,
  sendFlatProgress,
  setPaymentPageTexts,
  subObjectConverter,
} from '@src/utils';

import {
  formInsurancePeriodDefaultValues,
  formInsurancePeriodSchema,
} from '../../schemas';
import {
  ContentWrapper,
  PromoSubmitButton,
  PromoTextInput,
  PromoWrapper,
} from './form-insurance-period.styles';
import {
  useFlatOrderArray,
  useIflFlatDraft,
  useIflOrderFlatData,
  useRequestData,
} from './hooks';

export const FormInsurancePeriod: FC = () => {
  const queryClient = useQueryClient();
  const theme: any = useTheme();
  const [isInputActive, setIsInputActive] = useState(false);
  const { t } = useTranslation();
  const {
    state: {
      stateFormIFLFlat: {
        promoCode,
        promoCodeApplyed,
        risks,
        insuranceSubproducts,
        selectedDuration,
        selectedRegion,
        selectedDate,
        selectedIProduct,
        getPricesFull,
        dadaValue,
        presetData,
      },
      stateAuth: { authTokens },
      stateUser: { profile, promoCodeFailedMessage, agentLogin, preset },
      stateAuthFlow: { authFlowParams, authStep, sepScrAuth },
      stateWizard: { currentStep },
      statePresetSetting: { isPresetSettingDisplayed },
    },
    dispatch,
  } = useContext(Store);

  const setFlatOrderArray = useFlatOrderArray();
  const { productCode, productVersion } = useMemo(
    () => parseProductCode(selectedIProduct?.code),
    [selectedIProduct?.code]
  );

  const [minDate, setMinDate] = useState<Date>(new Date());
  const [maxDate, setMaxDate] = useState<Date>(new Date());
  const [dateEqual, setDateEqual] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormPromoProps>(
    formInsurancePeriodDefaultValues
  );
  const [submitPromoDisabled, setSubmitPromoDisabled] = useState(false);
  const [autoInitPromo, setAutoInitPromo] = useState(false);
  const [localPromo, setLocalPromo] = useState<string | undefined>();
  const [isCustomerEventSended, setIsCustomerEventSended] = useState(false);

  const apartmentSubscriptionTypeFlag = useMemo(
    () =>
      authFlowParams?.partnerAuthRegType === 'base'
        ? FeatureFlags.ApartmentSubscriptionType
        : (null as unknown as FeatureFlags),
    [authFlowParams?.partnerAuthRegType]
  );

  const { profileRefetch, profileIsLoading, profileError } =
    useValidateProfileAuth();

  useBlockNextStep(profileIsLoading, profileError);

  const { refetch: refetchCustomerEventRegister } = useCustomerEventRegister({
    eventCode: analyticEvents.iflFlatStep5Period,
    contextData: {
      address: dadaValue?.value,
      risks: getMainRiskCodesToString(risks),
      additionalRisks: getAdditionalRiskCodesToString(risks),
      subObjects:
        insuranceSubproducts?.subObjects &&
        JSON.stringify(subObjectConverter(insuranceSubproducts.subObjects)),
    },
  });

  useEffect(() => {
    if (!preset) {
      sendAnalyticEvent(analyticEvents.iflStep5Period);
      sendAnalyticEvent(analyticEvents.iflFlatStep5Period);
    }
  }, [preset]);

  useEffect(() => {
    if (
      !isCustomerEventSended &&
      !!risks &&
      !!insuranceSubproducts?.subObjects
    ) {
      refetchCustomerEventRegister();
      setIsCustomerEventSended(true);
    }
  }, [risks, insuranceSubproducts?.subObjects]);

  const { subobjectsError, isPricesLoading, pricesError, pricesRes } =
    useRequestData(autoInitPromo, localPromo);

  const navigate = useNavigate();
  const orderData = useIflOrderFlatData();
  useIflFlatDraft();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
    reset,
    getValues,
    watch,
  } = useForm<FormPromoProps>({
    resolver: yupResolver(formInsurancePeriodSchema),
    shouldFocusError: true,
    mode: 'all',
    defaultValues: {
      promoCode:
        promoCodeApplyed && getPricesFull?.isSuccessfulPromo
          ? promoCodeApplyed
          : promoCode,
    },
  });

  useEffect(() => {
    if (selectedDate !== undefined && selectedDate !== null) {
      localStorage.setItem('selectedDate', JSON.stringify(selectedDate));
    }
  }, [selectedDate]);

  useEffect(() => {
    const subscription = watch((value) => {
      dispatch({
        type: IFLFlatActionTypes.SetPromoCode,
        payload: value.promoCode,
      });
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const setStepUpdated = () => {
    dispatch({
      type: WizardActionTypes.SetUpdateFormState,
      payload: true,
    });
  };

  const { promoCode: promoCodeValue } = getValues();
  resetPromo(promoCodeValue, promoCodeApplyed, null, watch('promoCode'));

  useEffect(() => {
    dispatch({
      type: WizardActionTypes.SetFwNavDisabled,
      payload: false,
    });

    if (promoCodeApplyed && getPricesFull?.isSuccessfulPromo) {
      setLocalPromo(promoCodeApplyed);
      setAutoInitPromo(true);
    }
  }, []);

  const setSelectedDate = (newDate: Date) => {
    setStepUpdated();

    dispatch({
      type: IFLFlatActionTypes.SetSelectedDate,
      payload: newDate,
    });
    dispatch({
      type: WizardActionTypes.SetFwNavDisabled,
      payload: false,
    });

    if (!preset) {
      sendAnalyticEvent(analyticEvents.dateStartRealty);
    }
  };

  const onRadioSelector = (val: SelectedDuration) => {
    setStepUpdated();

    dispatch({
      type: IFLFlatActionTypes.SetSelectedDuration,
      payload: val,
    });

    if (!preset) {
      sendAnalyticEvent(analyticEvents.periodSelectRealty, {
        period: durationDescription[val],
      });
    }
  };

  const {
    isLoading: isInsuranceLimitStartLoading,
    error: insuranceLimitStartError,
    res: insuranceLimitStartRes,
    refetch: refetchInsuranceLimitStart,
  } = useRequest(
    'formIFLGetInsuranceLimitStart',
    'get',
    `${ApiEndpoints.INSURANCE_LIMIT_START}/${productCode}/${productVersion}`,
    [productCode, productVersion]
  );

  const validatePage = useCallback(() => {
    if (!insuranceSubproducts) {
      return false;
    }

    dispatch({
      type: CheckUserDataActionTypes.SetProductCode,
      payload: selectedIProduct?.code,
    });
    dispatch({
      type: CheckUserDataActionTypes.SetRisks,
      payload: risks?.filter((riskItem) => riskItem.active),
    });
    dispatch({
      type: CheckUserDataActionTypes.SetSubobjects,
      payload: insuranceSubproducts?.subObjects,
    });
    dispatch({
      type: CheckUserDataActionTypes.SetPromoCode,
      payload: autoInitPromo ? localPromo : undefined,
    });
    dispatch({
      type: CheckUserDataActionTypes.SetAddress,
      payload: dadaValue?.value || '',
    });
    dispatch({
      type: CheckUserDataActionTypes.SetRegionCode,
      payload: selectedRegion?.region,
    });
    dispatch({
      type: CheckUserDataActionTypes.SetAddressCode,
      payload: dadaValue?.data?.fias_id || DEFAULT_FIAS_ID,
    });
    dispatch({
      type: CheckUserDataActionTypes.SetPrices,
      payload: getPricesFull?.prices,
    });
    dispatch({
      type: CheckUserDataActionTypes.SetSelectedDuration,
      payload: selectedDuration || undefined,
    });
    dispatch({
      type: AvailableSubscriptionActionTypes.SetOrderData,
      payload: orderData,
    });

    if (!profile?.profile.lastName) {
      profileRefetch().then((data) => {
        if (
          data.error?.response?.status !== 403 &&
          data.error?.response?.data?.code !== 'INVALID_ROLE'
        ) {
          navigate('/check-user-data');
        } else {
          navigate('/personal-info', {
            state: { nextRoute: '/check-user-data' },
          });
        }
      });
    } else {
      navigate('/check-user-data');
    }

    return false;
  }, [
    selectedIProduct?.code,
    risks,
    insuranceSubproducts?.subObjects,
    autoInitPromo,
    localPromo,
    dadaValue?.value,
    dadaValue?.data?.fias_id,
    selectedRegion?.region,
    getPricesFull?.prices,
    selectedDuration,
    orderData,
  ]);

  useNextStep(validatePage);
  useIflFlatDraft();

  useEffect(() => {
    if (
      !(isPricesLoading || isInsuranceLimitStartLoading) &&
      !(insuranceLimitStartError || pricesError) &&
      selectedDate &&
      insuranceLimitStartRes?.startDate &&
      !checkDateBetween(
        convertDateByTimeZone(selectedDate)!,
        new Date(insuranceLimitStartRes?.startDate),
        new Date(insuranceLimitStartRes?.endDate)
      ) &&
      insuranceLimitStartRes?.data
    ) {
      setSelectedDate(
        createDateWithTimezone(insuranceLimitStartRes?.startDate)
      );
    }
  }, [
    !(isPricesLoading || isInsuranceLimitStartLoading) &&
      insuranceLimitStartRes,
    ,
    selectedDate,
  ]);

  useEffect(() => {
    if (
      !(insuranceLimitStartError || pricesError) &&
      selectedDate &&
      insuranceLimitStartRes?.startDate &&
      (checkDateBefore(
        new Date(selectedDate),
        new Date(insuranceLimitStartRes?.startDate)
      ) ||
        checkDateBefore(
          new Date(insuranceLimitStartRes?.endDate),
          new Date(selectedDate)
        ) ||
        dateEqual)
    ) {
      setSelectedDate(
        createDateWithTimezone(insuranceLimitStartRes?.startDate)
      );
    }
  }, [
    !(isPricesLoading || isInsuranceLimitStartLoading) &&
      insuranceLimitStartRes,
    dateEqual,
  ]);

  useEffect(() => {
    if (isPricesLoading || isInsuranceLimitStartLoading) {
      dispatch({
        type: WizardActionTypes.SetFwNavDisabled,
        payload: true,
      });

      setIsInputActive(false);
    }
  }, [isPricesLoading, isInsuranceLimitStartLoading]);

  const handleKeyPressEnter = () => {
    if (isInputActive) {
      submitPromoCode();
    } else if (currentStep !== authStep || sepScrAuth) {
      dispatch({
        type: WizardActionTypes.UpdateWantNextStep,
        payload: true,
      });
    }
  };
  useHandlePressKey(KeyCode.ENTER, handleKeyPressEnter, [isInputActive]);

  useEffect(() => {
    dispatch({
      type: OrderActionTypes.SetOrderPageTitle,
      payload: t('IFL_FLAT_FORM:headers.pulseApartment') || '',
    });
  }, []);

  useEffect(() => {
    setPaymentPageTexts(
      t('COMMON:hints.somethingHappensApartment'),
      checkValidDate(selectedDate) ? selectedDate : null
    );

    setFlatOrderArray(pricesRes);
  }, [
    isPricesLoading,
    isInsuranceLimitStartLoading,
    pricesRes,
    setFlatOrderArray,
    selectedDate,
  ]);

  useEffect(() => {
    if (!isPricesLoading && pricesRes) {
      dispatch({
        type: WizardActionTypes.SetFwNavDisabled,
        payload: false,
      });

      dispatch({
        type: IFLFlatActionTypes.SetGetPricesFull,
        payload: pricesRes,
      });

      if (!selectedDuration) {
        if (
          authFlowParams?.allowedPeriod ||
          authFlowParams?.defaultContractDuration
        ) {
          const duration =
            authFlowParams?.allowedPeriod ||
            authFlowParams?.defaultContractDuration;
          dispatch({
            type: IFLFlatActionTypes.SetSelectedDuration,
            payload: duration!,
          });
        } else if (pricesRes?.defaultContractDuration) {
          dispatch({
            type: IFLFlatActionTypes.SetSelectedDuration,
            payload: pricesRes?.defaultContractDuration,
          });
        } else {
          dispatch({
            type: IFLFlatActionTypes.SetSelectedDuration,
            payload: pricesRes?.prices[0]?.duration,
          });
        }
      }

      if (pricesRes?.isSuccessfulPromo === false && formData.promoCode !== '') {
        setError('promoCode', {
          type: 'string',
          message: 'COMMON:errors.promoCodeNotValid',
        });

        dispatch({
          type: UserActionTypes.SetPromoCodeFailedMessage,
          payload: t('COMMON:errors.promoCodeNotValid') || '',
        });
      }

      const isPromoCodeAppliedSuccessfully =
        pricesRes?.isSuccessfulPromo && promoCode !== '';

      let isButtonHasToBeDisabled = true;

      if (isPromoCodeAppliedSuccessfully) {
        isButtonHasToBeDisabled = true;
      }

      if (pricesRes?.isSuccessfulPromo === false && promoCode !== '') {
        isButtonHasToBeDisabled = false;
      }

      if (
        pricesRes?.isSuccessfulPromo === undefined &&
        promoCode !== '' &&
        !autoInitPromo
      ) {
        isButtonHasToBeDisabled = false;
      }
      if (
        pricesRes?.isSuccessfulPromo &&
        promoCode !== '' &&
        !promoCodeApplyed
      ) {
        dispatch({
          type: IFLFlatActionTypes.SetPromoCodeApplyed,
          payload: promoCode,
        });
      }

      setSubmitPromoDisabled(isButtonHasToBeDisabled);
    }
  }, [isPricesLoading, pricesRes]);

  useEffect(() => {
    if (!isInsuranceLimitStartLoading && insuranceLimitStartRes) {
      dispatch({
        type: WizardActionTypes.SetFwNavDisabled,
        payload: false,
      });

      setMinDate(new Date(insuranceLimitStartRes?.startDate));
      setMaxDate(new Date(insuranceLimitStartRes?.endDate));
      if (
        JSON.stringify(insuranceLimitStartRes?.startDate) ===
        JSON.stringify(insuranceLimitStartRes?.endDate)
      ) {
        setDateEqual(true);
      } else setDateEqual(false);
      setSelectedDate(
        createDateWithTimezone(
          selectedDate || insuranceLimitStartRes?.startDate
        )
      );
    }
  }, [isInsuranceLimitStartLoading, insuranceLimitStartRes]);

  useEffect(() => {
    if (authTokens?.authorization?.accessToken) {
      reset();
    }
  }, [authTokens?.authorization?.accessToken]);

  useEffect(() => {
    if (pricesRes?.isSuccessfulPromo === false && promoCode !== '') {
      setError('promoCode', {
        type: 'string',
        message: 'COMMON:errors.promoCodeNotValid',
      });
      dispatch({
        type: IFLFlatActionTypes.SetPromoCodeApplyed,
        payload: undefined,
      });
    }
  }, [isPricesLoading, pricesRes, promoCode]);

  const checkSubmitState = (val: string) => {
    setSubmitPromoDisabled(val === promoCode);
  };

  const submitPromoCode = handleSubmit((data) => {
    dispatch({
      type: IFLFlatActionTypes.SetPromoCode,
      payload: data.promoCode,
    });

    dispatch({
      type: UserActionTypes.SetPromoCodeFailedMessage,
      payload: undefined,
    });

    dispatch({
      type: IFLFlatActionTypes.SetPromoCodeApplyed,
      payload: undefined,
    });

    setFormData(data);
    setAutoInitPromo(true);
    setLocalPromo(promoCode);
  });

  const getLabel = () => {
    const { promoCode: statePromoCode } = getValues();
    const isPromoCodeAppliedSuccessfully = pricesRes?.isSuccessfulPromo;

    return isPromoCodeAppliedSuccessfully && promoCode === statePromoCode
      ? t('COMMON:success.applied')
      : t('COMMON:success.apply');
  };

  useEffect(() => {
    if (!!presetData) {
      sendAnalyticEvent(analyticEvents.iflFlatPresetStep2Period);
    }
  }, []);

  const pricesByPeriod = useMemo(() => {
    if (!getPricesFull?.prices) {
      return [];
    }

    if (authFlowParams?.allowedPeriod) {
      return getPricesFull.prices.filter(
        (pricesByPeriod) =>
          pricesByPeriod.duration === authFlowParams.allowedPeriod
      );
    }

    return getPricesFull.prices;
  }, [getPricesFull, authFlowParams, isPresetSettingDisplayed]);

  const isOnlyOnePeriodAllowed = useMemo(() => {
    return pricesByPeriod.length === 1;
  }, [pricesByPeriod]);

  const handleClickSettingPreset = useCallback(() => {
    sendAnalyticEvent(analyticEvents.iflFlatPresetToConfig);
    navigate('/preset-settings');
    dispatch({
      type: PresetSettingActionTypes.SetIsPresetSettingDisplayed,
      payload: true,
    });
    dispatch({
      type: WizardActionTypes.SetCurrentStep,
      payload: FIRST_STEP_FOR_SETTINGS,
    });

    queryClient.resetQueries(FormIFLGetPricesFull);
  }, []);

  useEffect(() => {
    if (!!presetData) {
      dispatch({
        type: WizardActionTypes.SetIsPageLoading,
        payload:
          isPricesLoading ||
          isInsuranceLimitStartLoading ||
          profileIsLoading ||
          !getPricesFull ||
          !insuranceSubproducts,
      });
    } else {
      dispatch({
        type: WizardActionTypes.SetIsPageLoading,
        payload:
          isPricesLoading || isInsuranceLimitStartLoading || profileIsLoading,
      });
    }
  }, [
    presetData,
    getPricesFull,
    insuranceSubproducts,
    isPricesLoading,
    isInsuranceLimitStartLoading,
    profileIsLoading,
  ]);

  useEffect(() => {
    sendFlatProgress(FlatSteps.PAYMENT_PERIODS);
  }, []);

  if (subobjectsError?.response?.data?.code === 'USER_DATA_ERROR') {
    navigate('/score-error');
  }

  if (pricesError?.response?.data?.code === 'OBJECT_DATA_ERROR') {
    dispatch({
      type: IFLHouseActionTypes.SetIsObjectDataError,
      payload: true,
    });

    navigate('/score-error');
  }

  if (!!presetData) {
    if (
      isPricesLoading ||
      isInsuranceLimitStartLoading ||
      !getPricesFull ||
      !insuranceSubproducts
    ) {
      return <Skeleton />;
    }
  } else {
    if (isPricesLoading || isInsuranceLimitStartLoading) return <Skeleton />;
  }

  if (profileIsLoading) return <Skeleton />;

  if (insuranceLimitStartError || pricesError) {
    const error = insuranceLimitStartError || pricesError;
    const status = error?.response?.status;

    if (status === 401) {
      dispatch({
        type: AuthActionTypes.SetAuthorizeFailState,
        payload: {
          title: t('COMMON:errors.authorizationError'),
          subtitle: t('COMMON:errors.retryRegistration') || '',
          refRoute: '/personal-info',
        },
      });

      dispatch({
        type: WizardActionTypes.SetCurrentStep,
        payload: 1,
      });

      navigate('/authorize-fail');
    }
    return <GlobalErrorInfo retryHandler={refetchInsuranceLimitStart} />;
  }

  if (profileError) {
    return <GlobalErrorInfo retryHandler={profileRefetch} />;
  }

  return (
    <Container>
      <AdaptiveHeadersWrapper>
        <SettingsWrapper>
          <PaymentPeriodsTitle
            featureFlag={apartmentSubscriptionTypeFlag}
            marginBottom={0}
            isOnlyOnePeriodAllowed={isOnlyOnePeriodAllowed}
          />
          {!!presetData && (
            <Button variant="setting" onClick={handleClickSettingPreset}>
              {t('COMMON:buttons.settingSubscription')}
            </Button>
          )}
        </SettingsWrapper>
        {agentLogin && (
          <SubHeadersWrapper>
            <SubHeaderAdaptiveWarning>
              {t('COMMON:hints.priceMayChange')}
            </SubHeaderAdaptiveWarning>
          </SubHeadersWrapper>
        )}
      </AdaptiveHeadersWrapper>

      <PaymentPeriodList
        onChange={onRadioSelector}
        selectedDuration={selectedDuration}
        prices={pricesByPeriod}
        isOnlyOnePeriodAllowed={isOnlyOnePeriodAllowed}
        getLabel={(price, isFeatureSubscriptionType) =>
          isFeatureSubscriptionType ? (
            <PaymentPeriodCardTitle
              premiumAndDelta={price.premiumAndDelta}
              premiumAndDeltaPromo={price.premiumAndDeltaPromo}
            />
          ) : (
            RadioLabelRender(price)
          )
        }
        getDescription={(price, isFeatureSubscriptionType) => {
          if (isOnlyOnePeriodAllowed) {
            return;
          }

          return isFeatureSubscriptionType ? (
            <PaymentPeriodCardDescription duration={price.duration} />
          ) : (
            RadioDescriptionRender(price)
          );
        }}
        getSubtitle={(price) => getPaymentPeriodCardSubtitle(price.duration)}
        featureFlag={apartmentSubscriptionTypeFlag}
      />

      <ContentWrapper>
        <PromoWrapper>
          <PromoTextInput>
            <Controller
              control={control}
              name="promoCode"
              render={({ field: { onChange, value }, fieldState }) => (
                <HelperText
                  status={
                    fieldState.error || promoCodeFailedMessage
                      ? 'error'
                      : 'success'
                  }
                  message={
                    (errors.promoCode?.message &&
                      t(errors.promoCode.message)) ||
                    promoCodeFailedMessage ||
                    (pricesRes?.isSuccessfulPromo &&
                    promoCode !== '' &&
                    getPricesFull?.prices ? (
                      <PromoCodeDiscount
                        prices={getPricesFull?.prices}
                        featureFlag={FeatureFlags.ApartmentDiscount}
                      />
                    ) : (
                      ''
                    ))
                  }
                  testId="apartment-form-promocode-error"
                >
                  <Input
                    label={t('COMMON:labels.promoCode') || ''}
                    value={value}
                    onChange={(val) => {
                      checkSubmitState(val);
                      return onChange(val);
                    }}
                    error={!!errors.promoCode || !!promoCodeFailedMessage}
                    onFocus={() => setIsInputActive(true)}
                    onBlur={() => setIsInputActive(false)}
                    disabled={isPricesLoading || isInsuranceLimitStartLoading}
                    {...addTestAttribute('apartment-form-promocode')}
                  />
                </HelperText>
              )}
            />
          </PromoTextInput>
          <PromoSubmitButton>
            <Button
              label={getLabel()}
              onClick={submitPromoCode}
              disabled={submitPromoDisabled}
              variant="secondary-2"
              adaptiveWidth
              {...addTestAttribute('apartment-form-promocode-button-apply')}
            />
          </PromoSubmitButton>
        </PromoWrapper>
      </ContentWrapper>

      <FormSubTitle>{t('COMMON:labels.subscriptionActive')}</FormSubTitle>
      <HelperText
        message={
          !(isPricesLoading || isInsuranceLimitStartLoading) &&
          !dateEqual && (
            <PaymentPeriodHint
              featureFlag={apartmentSubscriptionTypeFlag}
              minDate={minDate}
              maxDate={maxDate}
            />
          )
        }
        status="default"
        messagePosition="down"
        testId="apartment-form-subscription-start-date-error"
      >
        {isPricesLoading || isInsuranceLimitStartLoading ? (
          <Skeleton />
        ) : dateEqual ? (
          <InfoWrapper>
            <Info width={24} color={theme.colors.icon.primary} />
            <WarnigText
              {...addTestAttribute('apartment-form-subscription-start-message')}
            >
              {t('COMMON:labels.subscriptionWillStart')}{' '}
              {minDate.toLocaleDateString('ru-RU', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </WarnigText>
          </InfoWrapper>
        ) : (
          <Datepicker
            onChange={setSelectedDate}
            selected={checkValidDate(selectedDate) ? selectedDate : null}
            minDate={convertDateByTimeZone(minDate)}
            maxDate={convertDateByTimeZone(maxDate)}
            {...addTestAttribute('apartment-form-subscription-start')}
          />
        )}
      </HelperText>
    </Container>
  );
};
